import type { DateTime } from "luxon";
import type { SpansState } from "@/store/spans/state";
import type { PerspectiveViewConfig } from "@/models/perspective";
import type { MergeOptions } from "@/utils/objectMerging";
import type { SpansInMemoryFilters } from "@/store/spans/state";
import { makeState } from "@/store/spans/state";
import { mergeIntoOriginal } from "@/utils/objectMerging";

/**
 * Sets latestQueryStartedAt value from the Spans module.
 * @param state - Vuex state for the Spans module.
 * @param value - DateTime value to be assigned to latestQueryStartedAt.
 */
export function setLatestQueryStartedAt(
  state: SpansState,
  value: DateTime
): void {
  state.latestQueryStartedAt = value;
}

/**
 * Sets the loading state of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @param value - Boolean value to be assigned to loading.
 */
export function setLoading(state: SpansState, value: boolean): void {
  state.loading = value;
}

/**
 * Sets the boolean value for shouldExecuteQuery.
 * @param state - Vuex state for the Spans module.
 * @param value - Boolean value to be assigned to shouldExecuteQuery.
 */
export function setShouldExecuteQuery(state: SpansState, value: boolean): void {
  state.shouldExecuteQuery = value;
}

/**
 * Sets the updateSpans value of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @param value - Numeric value to be assigned to updateSpans.
 */
export function setUpdateSpans(state: SpansState, value: number): void {
  state.updateSpans = value;
}

/**
 * Set the splitByAttribute value of the Metrics module.
 * @param state - Vuex state for the Metrics module.
 * @param value - Value to be assigned to splitByAttribute.
 */
export function setSplitByAttribute(
  state: SpansState,
  value: string | undefined
): void {
  state.splitByAttribute = value;
}

/**
 * Sets the fetchLimit value of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @param value - Numeric value to be assigned to fetchLimit.
 */
export function setFetchLimit(state: SpansState, value: number): void {
  state.fetchLimit = value;
}

/**
 * Sets the streamingIsPaused value of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @param value - Boolean flag to be assigned to streamingIsPaused.
 */
export function setStreamingIsPaused(state: SpansState, value: boolean): void {
  state.streamingIsPaused = value;
}

/**
 * Sets the boolean value used to show or hide the perspective table
 * based on existence of the spans table
 * @param state - Vuex state for the Spans module.
 * @param value - Boolean value to be assigned to shouldShowTable;
 */
export function setShouldShowTable(state: SpansState, value: boolean) {
  state.shouldShowTable = value;
}

/**
 * Sets the boolean value when new dynamic columns were found.
 * @param state - Vuex state for the Spans module.
 * @param value - Array to be assigned to dynamicColumns;
 */
export function setDynamicColumns(state: SpansState, value: Array<string>) {
  state.dynamicColumns = value;
}

/**
 * Sets the dataProcessed value in bytes of the Spans Vuex module.
 * @param state - Vuex state for the Spans module.
 * @param value - Bytes of data to be assigned to dataProcessed.
 */
export function setDataProcessed(state: SpansState, value: number) {
  state.dataProcessed = value;
}

/**
 * Sets the dataReturned value in bytes of the Spans Vuex module.
 * @param state - Vuex state for the Spans module.
 * @param value - Bytes of data to be assigned to dataReturned.
 */
export function setDataReturned(state: SpansState, value: number) {
  state.dataReturned = value;
}

/**
 * Sets the updateWarehouse value of the Spans module.
 * This state is watched to trigger grabbing data from
 * the perspective table and adding it to the warehouse.
 * @param state - Vuex state for the Spans module.
 * @param value - Numeric value to be assigned to updateWarehouse.
 */
export function setUpdateWarehouse(state: SpansState, value: number): void {
  state.updateWarehouse = value;
}

/**
 * Sets the number of spans that are already in memory.
 * @param state - Vuex state for the Spans module.
 * @param value - Numeric value to be assigned to spansCount
 */
export function setSpansCount(state: SpansState, value: number): void {
  state.spansCount = value;
}

/**
 * Sets the value of the hasReportedSpans state.
 * @param state - Vuex state for the Spans module.
 * @param value - Boolean value to be assigned to hasReportedSpans
 */
export function setHasReportedSpans(state: SpansState, value: boolean): void {
  state.hasReportedSpans = value;
}

/**
 * Sets the value of the tableConfiguration state.
 * @param state - Vuex state for the spans module.
 * @param value - PerspectiveViewConfig value to be assigned to tableConfiguration.
 */
export function setTableConfiguration(
  state: SpansState,
  value: PerspectiveViewConfig
): void {
  state.tableConfiguration = value;
}

/**
 * Sets the value of the applyConfiguration state. State used to trigger applying configuration.
 * @param state - Vuex state for the Spans module.
 * @param value - Boolean value to be assigned to applyConfiguration.
 */
export function setApplyConfiguration(state: SpansState, value: boolean): void {
  state.applyConfiguration = value;
}

/**
 * Sets the value of the tableSchema state.
 * @param state - Vuex state for the Spans module.
 * @param value - Record to be assigned to tableSchema.
 */
export function setTableSchema(
  state: SpansState,
  value: Record<string, string>
): void {
  state.tableSchema = value;
}

/**
 * Sets the value of the shouldClearTable state. State used to clear the table on filter change
 * @param state - Vuex state for the Spans module.
 * @param value - Boolean value to be assigned to shouldClearTable.
 */
export function setShouldClearTable(state: SpansState, value: boolean): void {
  state.shouldClearTable = value;
}

/**
 * Reset the state object to its initial value.
 * @param state - Vuex state for the module.
 * @param options - Key subset modifiers for the reset operation.
 */
export function reset(state: SpansState, options?: MergeOptions): void {
  mergeIntoOriginal(
    state as unknown as Record<string, unknown>,
    makeState() as unknown as Record<string, unknown>,
    options
  );
}

/**
 * Increment the updateSpans value of the Spans' module by 1.
 * @param state - Vuex state for the Spans module.
 */
export function incUpdateSpans(state: SpansState): void {
  state.updateSpans += 1;
}

/**
 * Replace the inMemoryFilters with a new ones.
 * @param state - Vuex state for the Spans module.
 * @param filters - Filter to be set to the inMemoryFilters object.
 */
export function setInMemoryFilters(
  state: SpansState,
  filters: SpansInMemoryFilters
): void {
  const copy = { ...state.inMemoryFilters };
  Object.assign(copy, filters);
  state.inMemoryFilters = copy;
}
